import { BaseManager } from '../core/base-manager';
import { ManagerRegistry } from '../core/manager-registry';
import { SectionDockPanel, type SectionDockAxis, type SectionDockType } from '../components/section-dock-panel';
import { themeManager } from '../services/theme';
import { localeManager } from '../services/locale';

export class SectionDockManager extends BaseManager {
    private panel: SectionDockPanel | null = null;
    private unsubscribeTheme: (() => void) | null = null;
    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeDockState: (() => void) | null = null;

    constructor(registry: ManagerRegistry) {
        super(registry);
    }

    public init(): void {
        if (!this.unsubscribeTheme) {
            this.unsubscribeTheme = themeManager.subscribe(() => {
                this.applyPresentation();
            });
        }

        if (!this.unsubscribeLocale) {
            this.unsubscribeLocale = localeManager.subscribe(() => {
                this.applyPresentation();
            });
        }

        if (!this.unsubscribeDockState) {
            this.unsubscribeDockState = this.registry.bottomDock?.onStateChange((state) => {
                if (state.id !== 'section') {
                    return;
                }
                if (!state.open) {
                    console.log("deactivateSection")
                    this.engineComponent?.deactivateSection();
                    return;
                }
                this.panel?.resetForOpen();
            }) ?? null;
        }
    }

    public destroy(): void {
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeDockState) {
            this.unsubscribeDockState();
            this.unsubscribeDockState = null;
        }

        this.panel?.destroy();
        this.panel = null;
        super.destroy();
    }

    public getPanelElement(): HTMLElement {
        if (!this.panel) {
            this.panel = new SectionDockPanel({
                defaultType: null,
                defaultAxis: 'x',
                defaultHidden: false,
                onTypeChange: (type, axis) => {
                    this.activateByType(type, axis);
                },
                onAxisChange: (axis) => {
                    if (this.panel?.getActiveType() === 'axis') {
                        this.engineComponent?.activeSection(axis);
                    }
                },
                onHideToggle: (hidden) => {
                    if (hidden) {
                        this.engineComponent?.hideSection();
                        return;
                    }
                    this.engineComponent?.recoverSection();
                },
                onReverse: () => {
                    this.engineComponent?.reverseSection();
                },
                onReset: (type, axis) => {
                    this.engineComponent?.deactivateSection();
                    this.activateByType(type, axis);
                },
                onFitToModel: () => {
                    this.engineComponent?.scaleSectionBox();
                },
                defaultFill: this.engineComponent?.getFillCutFace() ?? false,
                onFillToggle: (isFilled) => {
                    this.engineComponent?.setFillCutFace(isFilled);
                }
            });
            this.panel.init();
        }

        this.panel.resetForOpen();
        this.applyPresentation();
        return this.panel.element;
    }

    private activateByType(type: SectionDockType, axis: SectionDockAxis): void {
        if (type === 'face') {
            this.engineComponent?.activeSection('face');
            return;
        }
        if (type === 'box') {
            this.engineComponent?.activeSection('box');
            return;
        }
        this.engineComponent?.activeSection(axis);
    }

    private applyPresentation(): void {
        if (!this.panel) {
            return;
        }
        this.panel.setTheme(themeManager.getTheme());
        this.panel.setLocales();
    }
}
